import type { IGraphClient } from '../../../src/services/GraphService';

interface IListRef {
  id: string;
  name?: string;
  displayName?: string;
}

function statusOf(error: unknown): number | undefined {
  return (error as { statusCode?: number } | undefined)?.statusCode;
}

/**
 * A list's id from its title or address name, or undefined if the site has no such list.
 * Graph takes a title in place of an id, but some titles (ours start "Restricted: ") are refused
 * with a 400 rather than found, so when the quick lookup misses, the site's lists are read and matched.
 */
export async function findListId(graph: IGraphClient, siteId: string, names: string[]): Promise<string | undefined> {
  for (const name of names) {
    try {
      const list: { id: string } = await graph.api(`/sites/${siteId}/lists/${encodeURIComponent(name)}?$select=id`).get();
      return list.id;
    } catch (error) {
      const status: number | undefined = statusOf(error);
      if (status !== 404 && status !== 400) {
        throw error;
      }
    }
  }
  const wanted: string[] = names.map(n => n.toLowerCase());
  let next: string | undefined = `/sites/${siteId}/lists?$select=id,name,displayName&$top=200`;
  while (next) {
    const page: { value: IListRef[]; '@odata.nextLink'?: string } = await graph.api(next).get();
    const hit: IListRef | undefined = page.value.filter(l => wanted.indexOf((l.displayName ?? '').toLowerCase()) !== -1 || wanted.indexOf((l.name ?? '').toLowerCase()) !== -1)[0];
    if (hit) {
      return hit.id;
    }
    next = page['@odata.nextLink'];
  }
  return undefined;
}
